import { useEffect, useRef, useState } from 'react';

type CountdownState = {
  remaining: number;
  expired: boolean;
  restart: (seconds?: number) => void;
};

export function useCountdown(initialSeconds: number, autoStart = true): CountdownState {
  const [remaining, setRemaining] = useState(initialSeconds);
  const startedAtRef = useRef<number | null>(autoStart ? performance.now() : null);
  const durationRef = useRef(initialSeconds);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    function tick() {
      if (startedAtRef.current == null) return;
      const elapsedSec = (performance.now() - startedAtRef.current) / 1000;
      const next = Math.max(0, durationRef.current - Math.floor(elapsedSec));
      setRemaining(next);
      if (next > 0) {
        rafRef.current = window.setTimeout(tick, 250);
      }
    }
    if (autoStart) tick();
    return () => {
      if (rafRef.current) window.clearTimeout(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function restart(seconds?: number) {
    if (rafRef.current) window.clearTimeout(rafRef.current);
    durationRef.current = seconds ?? initialSeconds;
    startedAtRef.current = performance.now();
    setRemaining(durationRef.current);
    function tick() {
      if (startedAtRef.current == null) return;
      const elapsedSec = (performance.now() - startedAtRef.current) / 1000;
      const next = Math.max(0, durationRef.current - Math.floor(elapsedSec));
      setRemaining(next);
      if (next > 0) {
        rafRef.current = window.setTimeout(tick, 250);
      }
    }
    tick();
  }

  return {
    remaining,
    expired: remaining === 0,
    restart,
  };
}

export function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
